// src/utils/mapZoom.js

// Verifica si una layer pertenece a un sublayer visible. Antes solo miraba
// `lyr.options.pane`, pero los markers de centroide de los polígonos viven
// en `markerPane` (global, siempre visible), NO en el pane del sublayer —
// así que el filtro por `options.pane` no servía para ellos. Ahora también
// chequea `_arcgisOwnerPane` (custom property que seteamos al crear cada
// marker, apuntando al pane del sublayer dueño).
const _isLayerInVisiblePane = (map, lyr) => {
  // 1) Tag custom de sublayer dueño (markers de centroide de ArcGIS).
  if (lyr._arcgisOwnerPane) {
    const ownerPane = map.getPane?.(lyr._arcgisOwnerPane);
    if (ownerPane && ownerPane.style.display === "none") return false;
  }
  // 2) Pane nativo de la layer (polígonos SVG, etc).
  const paneName = lyr.options?.pane;
  if (paneName) {
    const pane = map.getPane?.(paneName);
    if (pane && pane.style.display === "none") return false;
  }
  return true;
};

// 🇵🇪 Zoom de reset a Perú con animación en 3 pasos
export const zoomResetPeru = (map) => {
  const target = [-9.19, -75.0152];
  const currentZoom = map.getZoom();
  const zoomOut = Math.max(currentZoom - 1.5, 5.2);
  const zoomMid = 5.8;
  const zoomFinal = 6.3;

  // Paso 1: zoom out suave desde donde esté
  map.flyTo(map.getCenter(), zoomOut, {
    duration: 0.9,
    easeLinearity: 0.15,
  });

  // Paso 2: acercar a Perú a un zoom intermedio
  setTimeout(() => {
    map.flyTo(target, zoomMid, {
      duration: 1.4,
      easeLinearity: 0.15,
    });
  }, 900);

  // Paso 3: zoom final un poco más cerca
  setTimeout(() => {
    map.flyTo(target, zoomFinal, {
      duration: 1.4,
      easeLinearity: 0.12,
    });
  }, 2300);
};

// Zoom a un punto concreto y abrir popup si coincide exactamente
export const zoomToPointAndOpen = (
  map,
  lat,
  lng,
  { zoom = 15, duration = 1.6 } = {}
) => {
  const target = [lat, lng];

  map.flyTo(target, zoom, { duration });

  setTimeout(() => {
    map.eachLayer((layer) => {
      if (layer.getLatLng && layer.openPopup) {
        // Skippeamos layers en panes ocultos.
        if (!_isLayerInVisiblePane(map, layer)) return;
        const ll = layer.getLatLng();
        if (!ll) return;

        if (
          Math.abs(ll.lat - lat) < 1e-6 &&
          Math.abs(ll.lng - lng) < 1e-6
        ) {
          try {
            layer.openPopup();
          } catch {}
        }
      }
    });
  }, 400);
};

// ✅ Versión suavizada usando flyToBounds + padding.
// `preferredLayer`: si se pasa, abrimos directamente su popup después del
// zoom — útil para búsquedas donde sabemos qué feature matcheó y queremos
// resaltarlo, sin depender de "el marker más cercano al centro" (que con
// varios matches dispersos puede no caer cerca de ninguno).
export const zoomToBoundsAndOpen = (
  map,
  bounds,
  { maxZoom = 15, duration = 1.0, padding = [40, 40], preferredLayer = null } = {}
) => {
  if (!bounds || !bounds.isValid || !bounds.isValid()) return;

  // Zoom suave a los bounds
  map.flyToBounds(bounds, {
    maxZoom,
    padding,
    duration: duration || 1.6,
    easeLinearity: 0.15,
  });

  const center = bounds.getCenter();

  const onMoveEnd = () => {
    map.off("moveend", onMoveEnd);

    setTimeout(() => {
      // 1) Si tenemos un layer preferido y está en un pane visible, abrimos
      //    SU popup directamente. Cubre el caso de búsqueda por nombre.
      if (
        preferredLayer &&
        typeof preferredLayer.openPopup === "function" &&
        _isLayerInVisiblePane(map, preferredLayer)
      ) {
        try {
          preferredLayer.openPopup();
        } catch {}
        return;
      }

      // 2) Fallback: marker visible más cercano al centro de los bounds.
      //    Útil cuando no sabemos qué feature matcheó (cache-hit branches).
      let best = null;
      let bestD = Infinity;

      map.eachLayer((lyr) => {
        if (
          typeof lyr.getLatLng === "function" &&
          typeof lyr.openPopup === "function"
        ) {
          if (!_isLayerInVisiblePane(map, lyr)) return;
          const ll = lyr.getLatLng();
          if (!ll) return;
          const d = map.distance(ll, center);
          if (d < bestD) {
            bestD = d;
            best = lyr;
          }
        }
      });

      // Threshold proporcional a los bounds — antes era fijo 50m, demasiado
      // estricto para parques industriales grandes donde los markers están
      // espaciados.
      const boundsDiagonal = map.distance(
        bounds.getSouthWest(),
        bounds.getNorthEast()
      );
      const threshold = Math.max(50, boundsDiagonal * 0.5);
      if (best && bestD <= threshold) {
        try {
          best.openPopup();
        } catch {}
      }
    }, 250);
  };

  map.on("moveend", onMoveEnd);
};

// ✅ Igual que el anterior pero partiendo de un featureLayer
export const zoomToFeatureLayerAndOpen = (
  map,
  featureLayer,
  { maxZoom = 15, duration = 1.0, padding = [40, 40] } = {}
) => {
  const b = featureLayer?.getBounds?.();
  if (!b || !b.isValid || !b.isValid()) return;

map.flyToBounds(b, {
  maxZoom,
  padding,
  duration: duration || 1.6,
  easeLinearity: 0.15,
});


  const center = b.getCenter();

  const onMoveEnd = () => {
    map.off("moveend", onMoveEnd);

    setTimeout(() => {
      let best = null;
      let bestD = Infinity;

      map.eachLayer((lyr) => {
        if (
          typeof lyr.getLatLng === "function" &&
          typeof lyr.openPopup === "function"
        ) {
          // Skippeamos layers en panes ocultos (ver _isLayerInVisiblePane).
          if (!_isLayerInVisiblePane(map, lyr)) return;

          const ll = lyr.getLatLng();
          if (!ll) return;

          const d = map.distance(ll, center);
          if (d < bestD) {
            bestD = d;
            best = lyr;
          }
        }
      });

      if (best && bestD <= 50) {
        try {
          best.openPopup();
        } catch {}
      }
    }, 250);
  };

  map.on("moveend", onMoveEnd);
};


window.zoomImage = function (src) {
  const overlay = document.createElement("div");
  overlay.className = "zoom-overlay";

  overlay.innerHTML = `
    <div class="zoom-box">
      <img src="${src}" class="zoom-img" alt="Imagen ampliada" />
    </div>
  `;

  overlay.onclick = () => overlay.remove();
  document.body.appendChild(overlay);
};
